import { assertStaffCapability, assertStaffRelationship, } from "./authorization.js";
import { boundedText } from "./bounded-text.js";
import { uuidV7Instant } from "./catalog.js";
import { detectCatalogImageType } from "./catalog-editing.js";
import { confirmationEvidenceKinds, } from "./concierge-request.js";
import { DomainRuleViolationError } from "./errors.js";
import { DomainValidationError } from "./identifiers.js";
import { isTerminalRequestState } from "./request-lifecycle.js";
// Supplier and internal fulfilment evidence of a concierge request (CHT-752, contract section 7.6). The staff record what the human
// work found out (a supplier contact, availability, a quote, a confirmation reference, why something is unavailable, a correction)
// in a staff-only table. A customer sees none of it. What a customer may read is a separate, staff-approved row (`CustomerUpdate`) and
// an attachment that staff shared on purpose. A request becomes CONFIRMED only on a fresh `CONFIRMATION_REFERENCE` row that staff
// recorded: never on model text, and never because a payment settled. No evidence row holds a payable amount: the only amount source
// of a payment stays the approved `payment_quotes` row (guards LF019 and LF020); a `QUOTE` row may point at one.
// Frozen: the database CHECK constraints are generated from these tuples.
export const fulfilmentEvidenceKinds = Object.freeze([
    "SUPPLIER_CONTACT",
    "AVAILABILITY",
    "QUOTE",
    "CONFIRMATION_REFERENCE",
    "UNAVAILABLE_REASON",
    "CORRECTION",
]);
export const fulfilmentContactMethods = Object.freeze([
    "PHONE",
    "EMAIL",
    "WHATSAPP",
    "IN_PERSON",
    "OTHER",
]);
/** What staff may tell the customer on a request. Each kind has the states it fits (`customerUpdateStates`). */
export const customerUpdateKinds = Object.freeze([
    "NEXT_STEP",
    "CONFIRMATION_DETAILS",
    "ALTERNATIVES",
    "UNAVAILABLE_NOTE",
    "RESTRICTIONS",
]);
export const attachmentVisibilities = Object.freeze(["STAFF_ONLY", "SHARED"]);
/** Documents a concierge may attach: a confirmation voucher or a photo. Nothing executable. */
export const requestAttachmentContentTypes = Object.freeze([
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/webp",
]);
export const REQUEST_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const FULFILMENT_TEXT_LIMITS = Object.freeze({
    supplierLabel: 200,
    referenceCode: 200,
    detail: 2000,
    customerUpdate: 1000,
    attachmentName: 120,
    attachmentPath: 255,
});
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** The staff clock may run a little ahead of the server's; a supplier timestamp further ahead is not a past event. */
const SOURCE_TIMESTAMP_SKEW_MS = 5 * 60 * 1000;
const invalid = (field, rule) => new DomainValidationError(field, rule);
function oneOf(field, value, allowed) {
    if (typeof value !== "string" || !allowed.includes(value)) {
        throw invalid(field, "must be a known value");
    }
    return value;
}
function optionalId(field, value) {
    if (value === null || value === undefined)
        return null;
    if (typeof value !== "string" || !UUID_SHAPE.test(value))
        throw invalid(field, "must be an id");
    return value.toLowerCase();
}
function optionalDate(field, value) {
    if (value === null || value === undefined)
        return null;
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
        throw invalid(field, "must be a valid time");
    }
    return value;
}
function optionalText(field, value, max, multiline = false) {
    if (value === null || value === undefined)
        return null;
    return boundedText(field, value, max, { multiline });
}
/**
 * Validates and normalizes one evidence row. The same rules are database CHECKs, so a row that passes here is a row the table accepts.
 *  - SUPPLIER_CONTACT: a supplier label and a contact method.
 *  - AVAILABILITY: optional label, method, expiry (a hold) and detail.
 *  - QUOTE: optional expiry, optional `paymentQuoteId`. The amount is never here.
 *  - CONFIRMATION_REFERENCE: `referenceCode` and `confirmationKind`, both required.
 *  - UNAVAILABLE_REASON: `detail` required.
 *  - CORRECTION: `supersedesEvidenceId` and `detail` required.
 * A field that does not belong to the kind is refused, so a row never carries a reference that is not a reference.
 */
export function validateFulfilmentEvidence(input, now) {
    const kind = oneOf("kind", input.kind, fulfilmentEvidenceKinds);
    const contactMethod = input.contactMethod === null || input.contactMethod === undefined
        ? null
        : oneOf("contactMethod", input.contactMethod, fulfilmentContactMethods);
    const supplierLabel = optionalText("supplierLabel", input.supplierLabel, FULFILMENT_TEXT_LIMITS.supplierLabel);
    const sourceTimestamp = optionalDate("sourceTimestamp", input.sourceTimestamp);
    if (sourceTimestamp !== null &&
        sourceTimestamp.getTime() > now.getTime() + SOURCE_TIMESTAMP_SKEW_MS) {
        throw invalid("sourceTimestamp", "must not be in the future");
    }
    const referenceCode = optionalText("referenceCode", input.referenceCode, FULFILMENT_TEXT_LIMITS.referenceCode);
    const confirmationKind = input.confirmationKind === null || input.confirmationKind === undefined
        ? null
        : oneOf("confirmationKind", input.confirmationKind, confirmationEvidenceKinds);
    const expiresAt = optionalDate("expiresAt", input.expiresAt);
    const paymentQuoteId = optionalId("paymentQuoteId", input.paymentQuoteId);
    const detail = optionalText("detail", input.detail, FULFILMENT_TEXT_LIMITS.detail, true);
    const supersedesEvidenceId = optionalId("supersedesEvidenceId", input.supersedesEvidenceId);
    if (kind === "SUPPLIER_CONTACT") {
        if (supplierLabel === null)
            throw invalid("supplierLabel", "is required for a supplier contact");
        if (contactMethod === null)
            throw invalid("contactMethod", "is required for a supplier contact");
    }
    if (kind === "CONFIRMATION_REFERENCE") {
        if (referenceCode === null)
            throw invalid("referenceCode", "is required for a confirmation");
        if (confirmationKind === null) {
            throw invalid("confirmationKind", "is required for a confirmation");
        }
    }
    else {
        if (referenceCode !== null)
            throw invalid("referenceCode", "belongs to a confirmation only");
        if (confirmationKind !== null) {
            throw invalid("confirmationKind", "belongs to a confirmation only");
        }
    }
    if (expiresAt !== null) {
        if (kind !== "QUOTE" && kind !== "AVAILABILITY") {
            throw invalid("expiresAt", "belongs to a quote or an availability");
        }
        if (expiresAt.getTime() <= now.getTime())
            throw invalid("expiresAt", "must be in the future");
    }
    if (paymentQuoteId !== null && kind !== "QUOTE") {
        throw invalid("paymentQuoteId", "belongs to a quote only");
    }
    if ((kind === "UNAVAILABLE_REASON" || kind === "CORRECTION") && detail === null) {
        throw invalid("detail", "is required");
    }
    if (kind === "CORRECTION") {
        if (supersedesEvidenceId === null) {
            throw invalid("supersedesEvidenceId", "is required for a correction");
        }
    }
    else if (supersedesEvidenceId !== null) {
        throw invalid("supersedesEvidenceId", "belongs to a correction only");
    }
    return Object.freeze({
        kind,
        contactMethod,
        supplierLabel,
        sourceTimestamp,
        referenceCode,
        confirmationKind,
        expiresAt,
        paymentQuoteId,
        detail,
        supersedesEvidenceId,
    });
}
// --- who, and in which states -------------------------------------------------------------------------------------------
/** Evidence is recorded on a request that staff hold and that is not closed. A DRAFT is the customer's own. */
export const fulfilmentEvidenceStates = Object.freeze([
    "SUBMITTED",
    "ASSIGNED",
    "IN_PROGRESS",
    "AWAITING_CUSTOMER",
    "PAYMENT_REQUIRED",
    "CONFIRMED",
]);
/**
 * The gate of every fulfilment command: the role (`request.work`, MFA and environment), then the relationship. The OWNER and a SUPERVISOR
 * work the request; the designated assignee may too, while the request is ASSIGNED (the same people who may mark it unavailable there). A
 * staff member with no such relationship is `relationship_missing` in every state, so the refusal cannot reveal the state.
 */
export function assertMayWorkFulfilment(actor, environment) {
    const staff = assertStaffCapability(actor, "request.work", environment);
    assertStaffRelationship(staff.authority, staff.relationship, "WORK");
    return staff;
}
/** Why evidence cannot be recorded in a state, or `null` when it can. An ended request is `request.terminal`; a draft is not a staff matter. */
export function fulfilmentEvidenceStateRefusal(state) {
    if (isTerminalRequestState(state))
        return "request.terminal";
    return fulfilmentEvidenceStates.includes(state) ? null : "request.evidence_state_invalid";
}
export function assertFulfilmentEvidenceState(state) {
    const refusal = fulfilmentEvidenceStateRefusal(state);
    if (refusal !== null)
        throw new DomainRuleViolationError(refusal);
}
// --- customer updates ---------------------------------------------------------------------------------------------------
/**
 * The states in which each kind of customer update tells the truth. "Nothing says booked unless the state says so" (contract 7.5): a
 * confirmation detail needs a confirmed request, an unavailable note needs an unavailable one. The update is staff-written text that
 * a person approved by publishing it; it is never model text.
 */
export const customerUpdateStates = Object.freeze({
    NEXT_STEP: ["IN_PROGRESS", "AWAITING_CUSTOMER", "PAYMENT_REQUIRED", "CONFIRMED"],
    CONFIRMATION_DETAILS: ["CONFIRMED", "COMPLETED"],
    ALTERNATIVES: ["IN_PROGRESS", "AWAITING_CUSTOMER", "UNAVAILABLE", "DECLINED"],
    UNAVAILABLE_NOTE: ["UNAVAILABLE"],
    RESTRICTIONS: ["IN_PROGRESS", "AWAITING_CUSTOMER", "PAYMENT_REQUIRED", "CONFIRMED"],
});
export function validateCustomerUpdate(input) {
    return Object.freeze({
        kind: oneOf("kind", input.kind, customerUpdateKinds),
        text: boundedText("text", input.text, FULFILMENT_TEXT_LIMITS.customerUpdate, {
            multiline: true,
        }),
    });
}
export function assertCustomerUpdateFits(kind, state) {
    if (!customerUpdateStates[kind].includes(state)) {
        throw new DomainRuleViolationError("request.update_kind_invalid");
    }
}
/**
 * Whether a row may confirm a request. The rule of guard LF057, judged here first so the refusal has a code:
 *  - the row exists, is of this request, is a `CONFIRMATION_REFERENCE` and has a reference and a confirmation kind (else invalid);
 *  - it is FRESH: recorded at or after the version of the request's latest transition into IN_PROGRESS (a request that went to the
 *    customer or to payment and came back needs new evidence), and no `CORRECTION` supersedes it (else stale).
 * `latestInProgressVersion` is the version of the newest history row that ENTERED IN_PROGRESS; `null` means it never did.
 */
export function assessConfirmationEvidence(input) {
    const { evidence } = input;
    if (evidence === null ||
        evidence.requestId !== input.requestId ||
        evidence.kind !== "CONFIRMATION_REFERENCE" ||
        evidence.referenceCode === null ||
        evidence.confirmationKind === null) {
        return { ok: false, code: "request.evidence_invalid" };
    }
    if (input.latestInProgressVersion === null ||
        evidence.requestVersion < input.latestInProgressVersion ||
        input.supersededIds.has(evidence.id)) {
        return { ok: false, code: "request.evidence_stale" };
    }
    return { ok: true, kind: evidence.confirmationKind, reference: evidence.referenceCode };
}
// --- correction -------------------------------------------------------------------------------------------------------
/**
 * A CONFIRMED request can only go on to COMPLETED, or be cancelled by staff (R25): the transition table has no way back and this lane
 * adds none. A confirmation that turns out wrong is therefore corrected in place without editing anything:
 *   1. a `CORRECTION` evidence row points at the row it corrects (the prior row and its history stay);
 *   2. a customer update with an explicit correction marker tells the customer what changed;
 *   3. where the request cannot go on, staff cancel it (R25) with a reason.
 * The row to correct is the TIP of the chain that starts at the confirmation row: a row that nothing supersedes yet.
 */
export function correctionChainTip(startId, rows) {
    const next = new Map();
    for (const row of rows) {
        if (row.supersedesEvidenceId !== null)
            next.set(row.supersedesEvidenceId, row.id);
    }
    let tip = startId;
    for (let hops = 0; hops <= rows.length; hops += 1) {
        const following = next.get(tip);
        if (following === undefined)
            return tip;
        tip = following;
    }
    throw new DomainRuleViolationError("request.evidence_invalid");
}
const STORAGE_PATH = /^[A-Za-z0-9][A-Za-z0-9/_.-]*$/;
/**
 * The metadata of one attachment. The bytes are NOT stored by this lane (the existing upload pattern is catalog-only: see
 * `docs/engineering/concierge-requests.md`, section 4), so `storagePath` is an opaque key that staff or a later upload step provides. It
 * must sit under `requests/<requestId>/`, so a path cannot point at a catalog object or at another request's file.
 */
export function validateAttachment(requestId, input) {
    const displayName = boundedText("displayName", input.displayName, FULFILMENT_TEXT_LIMITS.attachmentName);
    const storagePath = boundedText("storagePath", input.storagePath, FULFILMENT_TEXT_LIMITS.attachmentPath);
    if (!STORAGE_PATH.test(storagePath) ||
        storagePath.includes("..") ||
        !storagePath.startsWith(`requests/${requestId}/`)) {
        throw invalid("storagePath", "must be a key under the request");
    }
    const contentType = oneOf("contentType", input.contentType, requestAttachmentContentTypes);
    if (!Number.isSafeInteger(input.sizeBytes) ||
        input.sizeBytes < 1 ||
        input.sizeBytes > REQUEST_ATTACHMENT_MAX_BYTES) {
        throw invalid("sizeBytes", `must be 1 to ${REQUEST_ATTACHMENT_MAX_BYTES}`);
    }
    return Object.freeze({ displayName, storagePath, contentType, sizeBytes: input.sizeBytes });
}
// --- attachment bytes (CHT-754) ----------------------------------------------------------------------------------------
// The bytes of an attachment go to a PRIVATE object store with a signed upload target, the same way catalog media does. The server
// makes the key; a client never sends a path or a file name that reaches it. The key is `requests/<requestId>/<uploadId>.<ext>`.
/** At most this many files on one request. */
export const REQUEST_ATTACHMENT_MAX_COUNT = 20;
/** At most this many bytes in all the files of one request. */
export const REQUEST_ATTACHMENT_MAX_TOTAL_BYTES = 50 * 1024 * 1024;
/** A pending upload is attached within this time of its target. A longer wait means a new target. */
export const REQUEST_ATTACHMENT_ATTACH_WINDOW_MS = 2 * 60 * 60 * 1000;
/** A signed download URL lives this long. */
export const REQUEST_ATTACHMENT_URL_TTL_SECONDS = 60;
const ATTACHMENT_EXTENSIONS = Object.freeze({
    "application/pdf": "pdf",
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
});
/** The key of one uploaded file. It holds ids and a fixed extension only: never a client file name. */
export function requestAttachmentObjectPath(requestId, uploadId, contentType) {
    return `requests/${requestId}/${uploadId}.${ATTACHMENT_EXTENSIONS[contentType]}`;
}
/** What the browser says about the file it will send: a known type and a size from 1 byte to 10 MiB. */
export function validateAttachmentUploadRequest(input) {
    const contentType = oneOf("contentType", input.contentType, requestAttachmentContentTypes);
    const { sizeBytes } = input;
    if (typeof sizeBytes !== "number" ||
        !Number.isSafeInteger(sizeBytes) ||
        sizeBytes < 1 ||
        sizeBytes > REQUEST_ATTACHMENT_MAX_BYTES) {
        throw invalid("sizeBytes", `must be 1 to ${REQUEST_ATTACHMENT_MAX_BYTES}`);
    }
    return { contentType, sizeBytes };
}
/**
 * The upload id of a pending upload must be a UUIDv7 made within the attach window. Judging the time stops an invented or old id
 * from naming an object. Messages name a field and a rule, never a path.
 */
export function checkAttachmentUploadId(uploadId, now) {
    const madeAt = typeof uploadId === "string" ? uuidV7Instant(uploadId) : null;
    if (typeof uploadId !== "string" || madeAt === null) {
        throw invalid("uploadId", "must be the id of an upload");
    }
    const age = now.getTime() - madeAt.getTime();
    // A little skew into the future is tolerated; an old id is not.
    if (age > REQUEST_ATTACHMENT_ATTACH_WINDOW_MS || age < -5 * 60 * 1000) {
        throw invalid("uploadId", "is an upload that has expired");
    }
    return uploadId;
}
/** The type that the first bytes of a file say it is, or `null`. PNG, JPEG, WebP and PDF only. */
export function detectRequestAttachmentType(bytes) {
    const image = detectCatalogImageType(bytes);
    if (image !== null)
        return image;
    const pdf = [0x25, 0x50, 0x44, 0x46, 0x2d];
    if (bytes.length >= pdf.length && pdf.every((value, index) => bytes[index] === value)) {
        return "application/pdf";
    }
    return null;
}
/**
 * Judges what the object store reports about an uploaded file: it exists, it is not empty or too large, its stored type is the
 * declared type, and its first bytes agree. Answers the real size. Throws a validation error (422). Names the rule, never the bytes.
 */
export function assertUploadedAttachment(expected, inspection) {
    if (!inspection.exists)
        throw invalid("file", "was not found in storage");
    if (inspection.byteSize < 1)
        throw invalid("file", "is empty");
    if (inspection.byteSize > REQUEST_ATTACHMENT_MAX_BYTES)
        throw invalid("file", "is larger than 10 MiB");
    if (inspection.contentType.split(";")[0]?.trim().toLowerCase() !== expected) {
        throw invalid("file", "has a type that is not the declared type");
    }
    if (detectRequestAttachmentType(inspection.leadingBytes) !== expected) {
        throw invalid("file", "has content that is not the type it claims");
    }
    return inspection.byteSize;
}
// Control characters, line and paragraph separators, and the bidirectional overrides that can make "fdp.exe" read as "exe.pdf".
// Written as code points on purpose: these characters are invisible in a source file.
const isUnsafeNameCodePoint = (code) => code <= 0x1f ||
    (code >= 0x7f && code <= 0x9f) ||
    (code >= 0x200b && code <= 0x200f) ||
    (code >= 0x2028 && code <= 0x202e) ||
    (code >= 0x2066 && code <= 0x2069) ||
    code === 0xfeff;
/**
 * The display name of a file, made safe to store and to show. It keeps the last part of a path (a browser may send a full path),
 * removes control and direction characters, collapses spaces and cuts to 120 characters. A name with nothing left becomes
 * `attachment.<ext>`. The name is a label only: it is never part of the storage key.
 */
export function sanitizeAttachmentDisplayName(raw, contentType) {
    const text = typeof raw === "string" ? raw : "";
    const lastPart = text.split(/[\\/]/).pop() ?? "";
    const wellFormed = [...lastPart]
        .map((character) => {
        const code = character.codePointAt(0) ?? 0;
        if (code >= 0xd800 && code <= 0xdfff)
            return "";
        return isUnsafeNameCodePoint(code) ? " " : character;
    })
        .join("");
    const cleaned = [...wellFormed.replace(/\s+/g, " ").trim().replace(/^\.+/, "")]
        .slice(0, FULFILMENT_TEXT_LIMITS.attachmentName)
        .join("")
        .trim();
    return cleaned === "" ? `attachment.${ATTACHMENT_EXTENSIONS[contentType]}` : cleaned;
}
/**
 * Whether one more file of `sizeBytes` fits the request: the count and the total size. `existing` is the display window of the
 * files of the request; a window that is cut counts as full (the limit is far below the window).
 */
export function assertAttachmentRoom(existing, sizeBytes) {
    const total = existing.items.reduce((sum, row) => sum + row.sizeBytes, 0);
    if (existing.truncated ||
        existing.items.length >= REQUEST_ATTACHMENT_MAX_COUNT ||
        total + sizeBytes > REQUEST_ATTACHMENT_MAX_TOTAL_BYTES) {
        throw new DomainRuleViolationError("request_attachment.limit_reached");
    }
}
